// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/Helpers/AnswerSettingsModal.js (343 lines).
 * BYTE-IDENTICAL — full drawer, replaces the T2 placeholder.
 *
 * V1 QUIRK preserved: v1 imports `getApiCall` from
 * `../../../../../service/getApiCall` (a distinct file, not httpCall).
 * v2 uses the equivalent from '@/service/httpCall' — same behavior.
 *
 * Preserved verbatim: language add/remove guards (`isLanguageInUse`,
 * `languagesLocked`, `hasExistingLanguageInUse`), Save-side re-check that
 * dropped-in-use codes get restored, `languageNames` built in the same
 * index order as `languages` (never re-derived from `allLanguages`), TTS
 * voice-profile selector (fetched from API_VOICE_PROFILE).
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import IvrDrawer from './IvrDrawer';
import { getApiCall } from '@/service/httpCall';
import { API_VOICE_PROFILE } from '@/service/url';
import { toast } from 'sonner';

const AnswerSettingsModal = ({
  show,
  onClose,
  webhookConfig,
  languages = [],
  ttsProfileId = '',
  onSave,
  webhookList = [],
  smsWebhookList = [],
  isLanguageInUse,
  languagesLocked = false,
}: any) => {
  const [draftType, setDraftType] = useState('');
  const [draftId, setDraftId] = useState('');
  const [allLanguages, setAllLanguages] = useState<any[]>([]);
  const [selectedLangs, setSelectedLangs] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [voiceProfiles, setVoiceProfiles] = useState<any[]>([]);
  const [draftTtsProfileId, setDraftTtsProfileId] = useState('');

  useEffect(() => {
    if (show && !loaded) {
      getApiCall('/v2/tts-languages').then((r: any) => {
        if (Array.isArray(r)) {
          setAllLanguages(r);
          setLoaded(true);
        }
      });
      getApiCall(API_VOICE_PROFILE).then((r: any) => {
        setVoiceProfiles(Array.isArray(r) ? r : []);
      });
    }
  }, [show, loaded]);

  useEffect(() => {
    if (show) {
      setDraftType(webhookConfig?.type || '');
      setDraftId(webhookConfig?.id ? String(webhookConfig.id) : '');
      setSelectedLangs(Array.isArray(languages) ? [...languages] : []);
      setDraftTtsProfileId(ttsProfileId ? String(ttsProfileId) : '');
    }
  }, [show, webhookConfig, languages, ttsProfileId]);

  const handleTypeChange = (e: any) => {
    setDraftType(e.target.value);
    setDraftId('');
  };

  const hasExistingLanguageInUse = languages.some(
    (code: any) => isLanguageInUse && isLanguageInUse(code),
  );

  const toggleLang = (code: any) => {
    if (languagesLocked) {
      toast.warning(
        `Remove the node(s) connected after the Language node before changing languages.`,
        { autoClose: 4000 },
      );
      return;
    }
    setSelectedLangs((prev) => {
      if (prev.includes(code)) {
        if (isLanguageInUse && isLanguageInUse(code)) {
          toast.warning(
            `This language is used in a downstream node. Remove the audio selection there first.`,
            { autoClose: 4000 },
          );
          return prev;
        }
        return prev.filter((c: any) => c !== code);
      }
      if (hasExistingLanguageInUse) {
        toast.warning(
          `Existing language(s) are already used in a downstream node. Remove that node's config before adding a new language.`,
          { autoClose: 4000 },
        );
        return prev;
      }
      return [...prev, code];
    });
  };

  const handleSave = () => {
    const droppedInUse = languages.filter(
      (code: any) =>
        !selectedLangs.includes(code) &&
        isLanguageInUse &&
        isLanguageInUse(code),
    );
    if (droppedInUse.length > 0) {
      toast.warning(
        `Cannot remove ${droppedInUse.length} language(s) still used in a downstream node. Remove that audio/config first.`,
        { autoClose: 4000 },
      );
      setSelectedLangs((prev) =>
        Array.from(new Set([...prev, ...droppedInUse])),
      );
      return;
    }

    const addedLangs = selectedLangs.filter(
      (code: any) => !languages.includes(code),
    );
    if (addedLangs.length > 0 && hasExistingLanguageInUse) {
      toast.warning(
        `Cannot add new language(s) while existing language(s) are used in a downstream node. Remove that node's config first.`,
        { autoClose: 4000 },
      );
      setSelectedLangs(languages);
      return;
    }

    const newWebhook =
      draftType && draftId ? { type: draftType, id: draftId } : null;
    // Build languageNames in the same order/index as selectedLangs so
    // languages[i] and languageNames[i] describe the same language.
    const languageNames = selectedLangs.map(
      (code: any) =>
        allLanguages.find((l: any) => l.code === code)?.language || code,
    );
    const ttsProfile = draftTtsProfileId
      ? voiceProfiles.find(
          (p: any) => String(p.id) === String(draftTtsProfileId),
        ) || null
      : null;
    onSave({
      webhookConfig: newWebhook,
      languages: selectedLangs,
      languageNames,
      ttsProfileId: draftTtsProfileId || '',
      ttsProfile,
    });
    onClose();
  };

  const activeList =
    draftType === 'webhook'
      ? webhookList
      : draftType === 'sms_webhook'
      ? smsWebhookList
      : [];

  return (
    <IvrDrawer
      show={show}
      onHide={onClose}
      title="Answer Node Settings"
      subtitle="Configure webhook and call flow languages."
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      {/* Webhook section */}
      <div className="ivr-drawer-section">
        <div className="ivr-drawer-section-title">Webhook</div>

        <div className="mb-2">
          <label className="form-label">Webhook Type</label>
          <select
            className="form-control form-select"
            value={draftType}
            onChange={handleTypeChange}
          >
            <option value="">None</option>
            <option value="webhook">Webhook</option>
            <option value="sms_webhook">SMS Webhook</option>
          </select>
        </div>

        {draftType && (
          <div className="mb-0">
            <label className="form-label">
              Select {draftType === 'webhook' ? 'Webhook' : 'SMS Webhook'}
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                className="form-control form-select"
                value={draftId}
                onChange={(e) => setDraftId(e.target.value)}
              >
                <option value="">Select...</option>
                {activeList.map((w: any) => (
                  <option key={w.id} value={w.id}>
                    {w.webhook_name || w.name}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                style={{ flexShrink: 0 }}
                onClick={() => {
                  setDraftType('');
                  setDraftId('');
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Languages section */}
      <div className="ivr-drawer-section">
        <div
          className="ivr-drawer-section-title"
          style={{ marginBottom: 12 }}
        >
          Languages
          <span
            style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}
          >
            <button
              onClick={() => {
                if (languagesLocked) {
                  toast.warning(
                    `Remove the node(s) connected after the Language node before changing languages.`,
                    { autoClose: 4000 },
                  );
                  return;
                }
                if (hasExistingLanguageInUse) {
                  toast.warning(
                    `Existing language(s) are already used in a downstream node. Remove that node's config before adding a new language.`,
                    { autoClose: 4000 },
                  );
                  return;
                }
                setSelectedLangs(allLanguages.map((l: any) => l.code));
              }}
              disabled={languagesLocked || hasExistingLanguageInUse}
              style={{
                fontSize: 10,
                fontWeight: 600,
                background: '#ede9fe',
                color: '#6366f1',
                border: 'none',
                borderRadius: 4,
                padding: '2px 8px',
                cursor:
                  languagesLocked || hasExistingLanguageInUse
                    ? 'not-allowed'
                    : 'pointer',
                opacity: languagesLocked || hasExistingLanguageInUse ? 0.5 : 1,
                letterSpacing: 0,
                textTransform: 'none',
              }}
            >
              Select All
            </button>
            <button
              onClick={() => {
                if (languagesLocked) {
                  toast.warning(
                    `Remove the node(s) connected after the Language node before changing languages.`,
                    { autoClose: 4000 },
                  );
                  return;
                }
                const inUse = selectedLangs.filter(
                  (c: any) => isLanguageInUse && isLanguageInUse(c),
                );
                if (inUse.length > 0) {
                  toast.warning(
                    `${inUse.length} language(s) are used in downstream nodes and cannot be removed.`,
                    { autoClose: 4000 },
                  );
                  setSelectedLangs(inUse);
                } else {
                  setSelectedLangs([]);
                }
              }}
              disabled={languagesLocked}
              style={{
                fontSize: 10,
                fontWeight: 600,
                background: '#fee2e2',
                color: '#ef4444',
                border: 'none',
                borderRadius: 4,
                padding: '2px 8px',
                cursor: languagesLocked ? 'not-allowed' : 'pointer',
                opacity: languagesLocked ? 0.5 : 1,
                letterSpacing: 0,
                textTransform: 'none',
              }}
            >
              Clear
            </button>
          </span>
        </div>

        {languagesLocked && (
          <p
            style={{
              color: '#ef4444',
              fontSize: 12.5,
              margin: '0 0 10px',
            }}
          >
            A node is connected after the Language node. Remove it first to
            change languages here.
          </p>
        )}

        {!languagesLocked && hasExistingLanguageInUse && (
          <p
            style={{
              color: '#ef4444',
              fontSize: 12.5,
              margin: '0 0 10px',
            }}
          >
            Existing language(s) are already used in a downstream node — new
            languages can't be added until that node's config is removed.
          </p>
        )}

        {allLanguages.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>
            No languages available.
          </p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '6px 12px',
            }}
          >
            {allLanguages.map((lang: any) => (
              <div key={lang.code} className="form-check">
                <input
                  type="checkbox"
                  id={`lang-${lang.code}`}
                  className="form-check-input"
                  checked={selectedLangs.includes(lang.code)}
                  disabled={languagesLocked}
                  onChange={() => toggleLang(lang.code)}
                />
                <label htmlFor={`lang-${lang.code}`} className="form-check-label">
                  {lang.language}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TTS Voice Profile section */}
      <div className="ivr-drawer-section">
        <div className="ivr-drawer-section-title">TTS Voice Profile</div>
        <div className="mb-0">
          <label className="form-label">
            Voice Profile{' '}
            <span style={{ color: '#94a3b8', fontWeight: 400 }}>
              (used by every TTS Play node in this flow)
            </span>
          </label>
          <select
            className="form-control form-select"
            value={draftTtsProfileId}
            onChange={(e) => setDraftTtsProfileId(e.target.value)}
          >
            <option value="">
              {voiceProfiles.length === 0
                ? 'No profiles available'
                : 'Select profile...'}
            </option>
            {voiceProfiles.map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.flow_name || `Profile #${p.id}`} — {p.gender} | {p.language}
              </option>
            ))}
          </select>
        </div>
      </div>
    </IvrDrawer>
  );
};

export default AnswerSettingsModal;
